import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Loader2 } from 'lucide-react';
import { useAuth } from './AuthContext';
import SharedStyles from './components/shared/SharedStyles';
import { authService } from './services/authService';
import logger, { safeErrorEventDetails } from './utils/logger';

function AuthCallback() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState('');

  useEffect(() => {
    const handleCallback = async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const handoffToken = searchParams.get('handoffToken');
      const requestedDestination = searchParams.get('destination');
      const destination = requestedDestination || 'dashboard';

      // Remove the one-time handoff token from the URL as early as possible.
      window.history.replaceState({}, document.title, window.location.pathname);

      if (!handoffToken) {
        setError('Missing secure session handoff');
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      try {
        const data = await authService.exchangeNativeSessionHandoff(handoffToken);

        login(
          { accessToken: data.accessToken, refreshToken: data.refreshToken || null },
          data.user,
          data.household,
          data.onboarding,
          data.billing || null,
          data.features || {}
        );

        const normalizedDestination = destination.startsWith('/') ? destination : `/${destination}`;

        // Redirect to destination
        setTimeout(() => {
          if (!data.onboarding?.completed) {
            if (requestedDestination) {
              navigate(normalizedDestination, { replace: true });
              return;
            }
            const step = data.onboarding?.currentStep || 1;
            navigate(`/onboarding?step=${step}`, { replace: true });
          } else {
            navigate(normalizedDestination, { replace: true });
          }
        }, 500);

      } catch (error) {
        logger.errorEvent('native_session_handoff_callback_failed', safeErrorEventDetails(error));
        logger.error('Auth callback error:', error);
        setError('Authentication failed');
        setTimeout(() => navigate('/login'), 2000);
      }
    };

    handleCallback();
  }, [navigate, login]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{
      background: `
        radial-gradient(900px circle at 50% 15%, rgba(230, 223, 195, 0.08), transparent 55%),
        linear-gradient(180deg, var(--color-bg), var(--color-bg-secondary))
      `
    }}>
      <SharedStyles />

      <div className="max-w-md w-full rounded-2xl border border-[var(--color-border-strong)] bg-[var(--color-surface)]/95 p-8 text-center shadow-[var(--shadow-card)] backdrop-blur-xl">
        {error ? (
          <>
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border border-[var(--color-border-strong)] bg-[var(--color-surface-input)]">
              <span className="text-4xl">❌</span>
            </div>
            <h2 className="text-2xl font-bold text-[var(--color-text-primary)] mb-2">Authentication Failed</h2>
            <p className="text-[var(--color-text-muted)] mb-4">{error}</p>
            <p className="text-sm text-[var(--color-text-muted)]">Redirecting to login...</p>
          </>
        ) : (
          <>
            <Loader2 className="w-16 h-16 text-[var(--color-text-primary)] mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl font-bold text-[var(--color-text-primary)] mb-2">Logging you in...</h2>
            <p className="text-[var(--color-text-muted)]">Please wait a moment</p>
          </>
        )}
      </div>
    </div>
  );
}

export default AuthCallback;
